'use strict'

// handles all internationalization duties
class I18n{

  constructor(app){
    this._configs = {} // stores all texts and i18n settings for all languages
    this.lang = 'en'
    this.baseLang = 'en'
    this._desiredLangs = []
    this._app = app // keep reference to Vue instance
    this.isMetric = true
    this.DoWMapping = { 'Su': 0, 'Mo': 1, 'Tu': 2, 'We': 3, 'Th': 4, 'Fr': 5, 'Sa': 6} // mapping between schema.org DoW abbreviations and localized full names
    this.supportedLocales = ["ar","az","be","bg","bs","cs","da","de","el","en","es","es-mx","es-pe","et","fi","fil","fr","fr-ca","hr","hu","hy","id","it","ja","kk","ko","lt","lv","ms","nl","no","pl","pt","pt-br","ro","ru","sk","sl","sq","sr","sv","th","tr","uk","vi","zh","zh-cn","zh-tw"] // array inserted at build

    // supported i18 settings:
    // clock: 12- or 24- hours clock
    // rtl: language is read right-to-left
    this.i18nSettings = ['clock', 'rtl']
  }

  // loads text resource file and adds its content to message store.
  // sets baseLang (language code without country) from the resource file used.
  // input is assumed to be lower-case (performed by loader when setting the list)
  setLang(locales = []){
    if (typeof locales === 'string')
      locales = locales.split(',').map(x => x.trim())

    locales = this._sanitizeLocales(locales) // filter out invalid values
    this._desiredLangs = this._getLocalesPriorityList(locales) // build ordered list of locales to (try to) apply

    // get list of locales that have already been defined via addText method
    const customLocales = Object.keys(this._configs)

    // CE-195: list chinese locales that use traditional characters (in addition to zh-tw by default)
    const useChineseTrad = ['zh-hk', 'zh-mo']

    // select the locale that is supported
    let lang, isCustom = false
    for (let i = 0; i < this._desiredLangs.length; i++){
      const curlang = this._desiredLangs[i]
      const curBaseLang = curlang.split('-')[0]

      if (this.supportedLocales.includes(curlang) || (curlang !== curBaseLang && this.supportedLocales.includes(curBaseLang)) || useChineseTrad.includes(curlang)){
        lang = curlang
        break
      }
      else if (customLocales.includes(curlang) || (curlang !== curBaseLang && customLocales.includes(curBaseLang))){
        lang = curlang
        isCustom = true
        break
      }
      else
        console.log(`'${curlang}' not defined.`)
    }

    // not supposed to happen, but who has never experienced black magic fuckery?
    if (!lang)
      throw new Error('No supported language is defined. Requested: ' + locales)

    this.lang = lang
    const base = this.lang.split('-')[0]
    this.baseLang = base

    // override baselang for chinese locales that should use traditional writing
    if (base === 'zh' && useChineseTrad.includes(this.lang))
      this.baseLang ='zh-tw'

    if (isCustom)
      this._setTextDirection()
    else
      this._getTexts(base)
        .then(() => {this._setTextDirection()}) // wait to get texts & settings before setting directionality

    document.documentElement.setAttribute('lang', this.lang) // Set HTML lang attribute to help screen readers with pronunciation
    console.log('Selected locale: ' + this.lang)
    return this.lang
  }

  // fetches file and adds texts to i18n store
  // @lang: (string) base language to retrieve texts for
  async _getTexts(lang){
    let texts

    try{
      const res = await fetch(`../texts/${lang}.json`)
      if (res.status === 200)
        texts = await res.json()
      else
        throw new Error(`HTTP Code ${res.status}`)
    }
    catch(ex){
      console.log(`Error loading i18n file ${lang}.json.\n` + ex.message)
      return
    }

    // Adds texts to i18n message store.
    // file contains texts & i18n settings for a base language and its locales (e.g. es, es-ar and es-mx).
    Object.entries(texts).forEach(([lang, data]) => {
      this.addTexts(lang, data, true)
    })
  }

  // set directionality attributes
  _setTextDirection(){
    let direction = 'ltr', unused = 'rtl'
    const isRTL = (this._configs[this.lang] || this._configs[this.baseLang])?.rtl
    if (isRTL){
      [direction, unused] = [unused, direction]
    }

    document.documentElement.setAttribute('dir', direction)
    document.body.classList.add(direction)
    document.body.classList.remove(unused)

    if (this._app)
      this._app.rtl = isRTL
  }

  // remove invalid locale values from the list.
  // only does regex-based format validation, not really checking if values are correct
  _sanitizeLocales(locales){
    const rx = /^[a-z]{2,8}(-[a-z0-9]{2,})?$/
    return locales.map(locale => locale && locale.trim().toLowerCase()).filter(locale => rx.test(locale))
  }

  // Also accepts the following signature:
  //    getString(key, replacements, fallback)
  getString(key, pattern, value, fallback){
    if (pattern && typeof pattern === 'object')
      fallback = value

    // get text for selected language. Fallback to base language or message key as last resort
    var text = this._configs[this.lang]?.texts[key]
    if (!text && this.baseLang !== this.lang && this._configs[this.baseLang]?.texts)
      text = this._configs[this.baseLang].texts[key]

    if (!text)
      return fallback || key

    if (pattern){
      if (value)
        return text.replace(new RegExp(`{${pattern}}`, 'g'), value)
      else if (typeof pattern === 'object'){
        let replacedTxt = text
        Object.entries(pattern).forEach(([p, v])=> {
          replacedTxt = replacedTxt.replace(new RegExp(`{${p}}`, 'g'), v)
        })
        return replacedTxt
      }
    }
    else
      return text

    return (pattern && value) ? text.replace(new RegExp(`{${pattern}}`, 'g'), value) : text
  }

  convertKmToMiles(distance, precision = 1){
    return (distance * 0.621371).toFixed(precision)
  }

  formatDistance(value, shortAsMetres = false){
    var unit

    if (this.isMetric){
      if (value < 1 && shortAsMetres){
        value *= 1000
        unit = this.getString('M')
      }
      else
        unit = this.getString('KM')
    }
    else{
      value = this.convertKmToMiles(value)
      unit = 'mi' // convertKmToMiles returns a string with precision 1, so 1 mile is returned as 1.0 and plural applies.
    }
    return `${value} ${unit}`
  }

  formatAddress(store){
    var txt = this.getString('ADDRESSMACRO')
      .replace('{addr}', store.location.address || '')
      .replace('{zc}', store.location.zipCode || '')
      .replace('{city}', store.location.city || '')
    return txt
  }

  formatZipCodeState(store){
    let txt = '', countries = ['US', 'CA', 'MX', 'AU,', 'IT']
    if (countries.includes(store.location.country) && store.location.state)
      txt = store.location.state
    if (store.location.zipCode)
      txt += (txt ? " " : "") + store.location.zipCode
    return txt
  }

  // use built-in formatter, with fallback to currency code + value
  formatPrice(value, currency, maxDigits = 2){

    // CE-67: truncate decimal part if value is high and decimal part is 0
    // (We can't compare directly to 0 due to floating point precision issues).
    // This avoids displaying prices like 15,000.00
    if (value >= 1e3 && value % 1 < 0.01)
      maxDigits = 0

    let price
    const currentOpts = this.priceFormatter?.resolvedOptions()

    if (currentOpts?.currency === currency && currentOpts?.maximumFractionDigits === maxDigits)
      price = this.priceFormatter.format(value)
    else if (window.Intl?.NumberFormat){
      const opts = {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'symbol',
        maximumFractionDigits: maxDigits
      }
      this.priceFormatter = new Intl.NumberFormat(this.lang, opts)
      price = this.priceFormatter.format(value)
    }
    else
      price = currency + '\u202f' + value.toFixed(2)

    // Currency formatter inserts a non-breaking space between value and currency symbol,
    // but regular non-breaking space U+00A0 may break the layout by taking too much space (reason unknown).
    // Replace it with narrow no-break space (U+202F) to avoid this issue.
    // A non-breaking space is still desired to avoid a line break between the currency symbol and the value.
    return price.replace(/\u00A0/, '\u202f')
  }

  // gets the language to use for showing days of week
  _getDaysLanguage(){
    let lang = this.lang

    if (!this._configs[lang] || !this._configs[lang].texts.DAYS){
      if (this._configs[this.baseLang] && this._configs[this.baseLang].texts.DAYS)
        lang = this.baseLang
      else
        lang = 'en'
    }
    return lang
  }

  // returns localized version of the day component of a schema.org opening hours value
  getOpeningDays(input){
    const lang = this._getDaysLanguage()
    try {
      const daysText = this._configs[lang]?.texts?.DAYS

      if (!daysText) return input

      return input.replace(/[a-z]{2}/ig, match => {
        let day = daysText[this.DoWMapping[match]]
        return day
      })
      .replace(/,/g, ', ')
      .replace(/-/g, ' - ')
    } catch (e) {
      const errorMessage = `Error in getOpeningDays() - ${e.name}: ${e.message}\t` +
                            `input: ${input}\t` +
                            `lang: ${lang}\t` +
                            `configsForLang: ${JSON.stringify(this._configs?.[lang])}`;

      console.error(errorMessage);

      Tracker.log('error', {msg: errorMessage})

      return ''
    }
  }

  // returns localized version of the hours component of a schema.org opening hours value
  getOpeningHours(input){
    const lang = this._getDaysLanguage()
    try {
      input = input.replace(/-/g, ' - ').replace(/,/g, ', ')

      // 24h-clock: leave as is
      if (!this._configs[lang]?.clock || this._configs[lang]?.clock === 24)
        return input

      return input.replace(/(\d{2})[:hH\.](\d{2})/g, (match, hours, minutes) => {
        let h = (parseInt(hours, 10) % 12) || 12
        return `${h}:${minutes.padStart(2, '0')} ${hours < 12 || hours == 24 ? 'am': 'pm'}`
      })
    } catch (e) {
      console.error(
        `Error in getOpeningHours() - ${e.name}: ${e.message}`,
        `input: ${input}`,
        `lang: ${lang}`,
        `configsForLang: ${JSON.stringify(this._configs?.[lang])}`,
      )
      return ''
    }
  }

  // add/override texts, define new language
  addTexts(lang, texts = {}, defaultText = false){
    if (!lang){
      console.error('invalid langage')
      return
    }

    lang = lang.toLowerCase()

    // move options to their own object and remove them from labels object
    var options = {}
    this.i18nSettings.forEach(opt => {
      if (texts[opt]){
        options[opt] = texts[opt]
        delete texts[opt]
      }
    })

    if (!this._configs[lang] || !this._configs[lang].texts){
      this._configs[lang] = { texts: texts }
    }
    else{
      // language exists, update texts
      Object.entries(texts).forEach(([key, value]) => {
        // apply label if not default text or label is not already defined.
        // This is to not overwrite a template-script label with a default label.
        if (value && (!defaultText || this._configs[lang].texts[key] == null))
          this._configs[lang].texts[key] = value
      })
    }

    // set language options
    Object.entries(options).forEach(([key, value]) => {
      this._configs[lang][key] = value
    })

    // reset if texts added are custom translations OR texts are for the currently selected locale
    if (!defaultText || lang === this.lang || lang === this.baseLang)
      this._resetDefaultLang(lang)
  }

  // checks if the newly defined lang is in desired list with a higher priority than current lang
  _resetDefaultLang(newLang){
    // new texts for the current lang or base lang, force refresh
    if (newLang === this.lang || newLang === this.baseLang){
      if (this._app)
        this._app.forceUpdate()
      return
    }

    const currentIdx = this._desiredLangs.indexOf(this.lang),
          newLangIdx = this._desiredLangs.indexOf(newLang)

    // new/updated lang not in desired list: nothing to do
    if (newLangIdx === -1)
      return

    // new lang has higher priority than the current one: switch & refresh widget
    if (newLangIdx < currentIdx){
      const oldLang = this.lang,
            oldBase = this.baseLang

      this.lang = newLang
      this.baseLang = this.lang.split('-')[0]
      console.debug(`switch lang to '${this.lang}'`)
      document.documentElement.setAttribute('lang', this.lang)

      if (this._app){
        this._app.locale = this.lang
        this._app.baseLang = this.baseLang
        this._setTextDirection()
        this._app.forceUpdate() // force app to re-render after a language switch
        this._app.updateLocale(this.lang, oldLang, this.baseLang, oldBase)
      }
    }
  }

  // builds an ordered list of desired locales to check when a new lang is defined.
  // That allows knowing which locale should be used.
  // List is created by adding the base lang after each country-specific language, except if already present or if next element shares the same base lang
  _getLocalesPriorityList(locales){
    let modifs = [],
        newBases = []

    locales.forEach((locale, idx) => {
      const base = locale.split('-')[0]

      if (locale === base)
        return

      // insert base lang if not present AND following element does not have the same base lang
      const nextElem = locales[idx + 1],
            sameBase = nextElem && nextElem.startsWith(base + '-')

      if (!locales.includes(base) && !sameBase && !newBases.includes(base)){
        modifs.push({val: base, idx: idx + modifs.length + 1})
        newBases.push(base)
      }
    })

    let result = Utils.clone(locales)
    modifs.forEach(({val, idx}) => {
      result.splice(idx, 0, val)
    })

    // add browser languages, if not already present
    const userLang = (navigator.languages || [navigator.language]).map(x => x.toLowerCase())
    userLang.forEach(lang => {
      if (!result.includes(lang))
        result.push(lang)
    })

    // add 'en' as last-resort fallback if not already present
    if (!result.includes('en'))
      result.push('en')

    return result
  }

  /**
   * Retrieves a localized value from remote configuration options.
   * Checks for the given key and its translated versions using the current language and base language.
   *
   * @param {Record<string, any>} params - The remote configuration parameter object.
   * @param {string} optName - The name of the option to look up.
   * @param {*} fallback - The fallback value to return if no localized or default value is found.
   * @returns {*} The localized value if found, otherwise the base language value, or the default, or the fallback.
   */
  getLocalizedValue(params, optName, fallback){
    return params[`locale-${this.lang}-${optName}`]
      || params[`locale-${this.baseLang}-${optName}`]
      || params[optName]
      || fallback
  }
}
